import { useEffect, useState, useCallback } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  IconButton,
  Link,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import Wrapper from '../components/Wrapper'
import DialogAlert from '../components/DialogAlert'
import { useSessionStore } from '../state'
import {
  getPendingSubmissions,
  updateAccreditationStatus,
  UserWithAccreditation
} from '../services/accreditationService'
import { Timestamp } from 'firebase/firestore'

type ReviewAction = {
  userId: string
  displayName: string
  status: 'verified' | 'rejected'
}

function formatTimestamp(ts: Timestamp | null | undefined): string {
  if (!ts || typeof ts.toDate !== 'function') return '—'
  return ts.toDate().toLocaleDateString()
}

const AdminAccreditations = () => {
  const adminUid = useSessionStore(s => s.user?.uid)
  const role = useSessionStore(s => s.profile?.role)

  const [submissions, setSubmissions] = useState<UserWithAccreditation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [confirmAction, setConfirmAction] = useState<ReviewAction | null>(null)
  const [processing, setProcessing] = useState(false)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  const fetchSubmissions = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const results = await getPendingSubmissions()
      setSubmissions(results)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load submissions'
      )
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSubmissions()
  }, [fetchSubmissions])

  const toggleRow = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleConfirm = async () => {
    if (!confirmAction || !adminUid) return

    setProcessing(true)
    try {
      await updateAccreditationStatus(
        confirmAction.userId,
        confirmAction.status,
        adminUid
      )
      setSubmissions(prev => prev.filter(s => s.id !== confirmAction.userId))
      setSnackbar({
        open: true,
        message: `Successfully ${
          confirmAction.status === 'verified' ? 'approved' : 'rejected'
        } ${confirmAction.displayName}'s accreditation.`,
        severity: 'success'
      })
    } catch (err) {
      setSnackbar({
        open: true,
        message:
          err instanceof Error
            ? err.message
            : 'Failed to update accreditation status',
        severity: 'error'
      })
    } finally {
      setProcessing(false)
      setConfirmAction(null)
    }
  }

  if (role !== 'admin') {
    return (
      <Wrapper>
        <Alert severity='error'>
          Access denied. You must be an admin to view this page.
        </Alert>
      </Wrapper>
    )
  }

  return (
    <>
      <Wrapper>
        <Container maxWidth='lg'>
          <Paper sx={{ p: 3, mt: 2 }}>
            <Typography variant='h5' gutterBottom>
              Pending Accreditation Reviews
            </Typography>

            {loading && (
              <Box display='flex' justifyContent='center' py={4}>
                <CircularProgress />
              </Box>
            )}

            {error && (
              <Alert severity='error' sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {!loading && !error && submissions.length === 0 && (
              <Alert severity='info'>
                No pending accreditation submissions to review.
              </Alert>
            )}

            {!loading && !error && submissions.length > 0 && (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding='checkbox' />
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Cert Number</TableCell>
                      <TableCell>Document</TableCell>
                      <TableCell>Guided</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {submissions.map(submission => {
                      const guidedDocs =
                        submission.accreditation.guidedDiveDocuments
                      const hasGuidedDocs =
                        submission.guidedDiveFlag && !!guidedDocs
                      const isExpanded = expandedRows.has(submission.id)

                      return (
                        <>
                          <TableRow key={submission.id}>
                            <TableCell padding='checkbox'>
                              {hasGuidedDocs && (
                                <IconButton
                                  size='small'
                                  onClick={() => toggleRow(submission.id)}
                                  aria-label='expand row'
                                >
                                  {isExpanded ? (
                                    <KeyboardArrowUpIcon />
                                  ) : (
                                    <KeyboardArrowDownIcon />
                                  )}
                                </IconButton>
                              )}
                            </TableCell>
                            <TableCell>{submission.displayName}</TableCell>
                            <TableCell>
                              {submission.accreditation.type.toUpperCase()}
                              {submission.accreditation.level &&
                                ` — ${submission.accreditation.level}`}
                            </TableCell>
                            <TableCell>
                              {submission.accreditation.certificationNumber}
                            </TableCell>
                            <TableCell>
                              <Link
                                href={submission.accreditation.documentUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                              >
                                View Document
                              </Link>
                            </TableCell>
                            <TableCell>
                              {submission.guidedDiveFlag ? (
                                <Chip
                                  label='Guided'
                                  color='info'
                                  size='small'
                                />
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell>
                              <Box display='flex' gap={1}>
                                <Button
                                  variant='contained'
                                  color='success'
                                  size='small'
                                  disabled={processing}
                                  onClick={() =>
                                    setConfirmAction({
                                      userId: submission.id,
                                      displayName: submission.displayName,
                                      status: 'verified'
                                    })
                                  }
                                >
                                  Approve
                                </Button>
                                <Button
                                  variant='outlined'
                                  color='error'
                                  size='small'
                                  disabled={processing}
                                  onClick={() =>
                                    setConfirmAction({
                                      userId: submission.id,
                                      displayName: submission.displayName,
                                      status: 'rejected'
                                    })
                                  }
                                >
                                  Reject
                                </Button>
                              </Box>
                            </TableCell>
                          </TableRow>

                          {/* Expandable guided dive docs detail row */}
                          {hasGuidedDocs && (
                            <TableRow key={`${submission.id}-details`}>
                              <TableCell
                                colSpan={7}
                                sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}
                              >
                                <Collapse
                                  in={isExpanded}
                                  timeout='auto'
                                  unmountOnExit
                                >
                                  <Box sx={{ py: 2, pl: 4 }}>
                                    <Typography
                                      variant='subtitle2'
                                      gutterBottom
                                    >
                                      Guided Dive Professional Evidence
                                    </Typography>
                                    <Table size='small' sx={{ maxWidth: 600 }}>
                                      <TableBody>
                                        <TableRow>
                                          <TableCell
                                            component='th'
                                            sx={{ fontWeight: 600, width: 200 }}
                                          >
                                            Professional Status
                                          </TableCell>
                                          <TableCell>
                                            <Link
                                              href={
                                                guidedDocs!.professionalStatusUrl
                                              }
                                              target='_blank'
                                              rel='noopener noreferrer'
                                            >
                                              View Document
                                            </Link>
                                          </TableCell>
                                        </TableRow>
                                        <TableRow>
                                          <TableCell
                                            component='th'
                                            sx={{ fontWeight: 600 }}
                                          >
                                            Status Expiry
                                          </TableCell>
                                          <TableCell>
                                            {formatTimestamp(
                                              guidedDocs!
                                                .professionalStatusExpiry as Timestamp
                                            )}
                                          </TableCell>
                                        </TableRow>
                                        <TableRow>
                                          <TableCell
                                            component='th'
                                            sx={{ fontWeight: 600 }}
                                          >
                                            Liability Insurance
                                          </TableCell>
                                          <TableCell>
                                            <Link
                                              href={guidedDocs!.insuranceUrl}
                                              target='_blank'
                                              rel='noopener noreferrer'
                                            >
                                              View Document
                                            </Link>
                                          </TableCell>
                                        </TableRow>
                                        <TableRow>
                                          <TableCell
                                            component='th'
                                            sx={{ fontWeight: 600 }}
                                          >
                                            Insurance Expiry
                                          </TableCell>
                                          <TableCell>
                                            {formatTimestamp(
                                              guidedDocs!
                                                .insuranceExpiry as Timestamp
                                            )}
                                          </TableCell>
                                        </TableRow>
                                        {guidedDocs!.affiliatedCenter && (
                                          <TableRow>
                                            <TableCell
                                              component='th'
                                              sx={{ fontWeight: 600 }}
                                            >
                                              Affiliated Center
                                            </TableCell>
                                            <TableCell>
                                              {guidedDocs!.affiliatedCenter}
                                            </TableCell>
                                          </TableRow>
                                        )}
                                        <TableRow>
                                          <TableCell
                                            component='th'
                                            sx={{ fontWeight: 600 }}
                                          >
                                            Local Regulations
                                          </TableCell>
                                          <TableCell>
                                            {guidedDocs!
                                              .localRegulationsAcknowledged ? (
                                              <Box
                                                display='flex'
                                                alignItems='center'
                                                gap={0.5}
                                              >
                                                <CheckCircleIcon
                                                  color='success'
                                                  fontSize='small'
                                                />
                                                Acknowledged
                                              </Box>
                                            ) : (
                                              <Box
                                                display='flex'
                                                alignItems='center'
                                                gap={0.5}
                                              >
                                                <CancelIcon
                                                  color='error'
                                                  fontSize='small'
                                                />
                                                Not acknowledged
                                              </Box>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      </TableBody>
                                    </Table>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Container>
      </Wrapper>

      <DialogAlert
        open={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        title='Confirm Review Action'
        text={
          confirmAction
            ? `Are you sure you want to ${
                confirmAction.status === 'verified' ? 'approve' : 'reject'
              } ${confirmAction.displayName}'s accreditation?`
            : ''
        }
        confirmLabel={
          confirmAction?.status === 'verified' ? 'Approve' : 'Reject'
        }
        onConfirm={handleConfirm}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default AdminAccreditations
